<template>
  <div class="mt-4 loader w-100" v-if="!initiatives && isFetching">
    <initiative-skeleton class="initiative-skeleton" />
    <initiative-skeleton class="initiative-skeleton" />
    <initiative-skeleton class="initiative-skeleton" />
    <initiative-skeleton class="initiative-skeleton" />
  </div>
  <div v-else-if="initiatives && initiatives.length === 0">
    <slot name="empty-state" />
  </div>
  <div class="initiative-list" v-else>
    <initiatives-header
      @sort="sortInitiatives"
      @reset="resetSort"
      :sorted-by="sortedBy"
      :sorted-direction="sortedDirection"
      :show-filter-options="false"
      :show-field-settings-in-column="true"
      :hide-add-custom-field-icon="true"
    />
    <initiative-row
      v-for="initiative in initiatives"
      :key="initiative.id"
      :data-id="initiative.id"
      :orgId="initiative.organizationId"
      :krId="initiative.keyResultId"
      :goalId="initiative.goalId"
      :initiative="initiative"
      :initiatives="initiatives"
      :draggable="false"
      :show-parent="true"
      :selectable="selectable"
      :selectable-tasks="selectableTasks"
      @update-initiative="handleInitiativeUpdate"
      @update-initiatives="resetInitiatives"
      @delete-initiatives="deleteInitiatives"
    ></initiative-row>
  </div>
</template>

<script>
import InitiativeRow from '@/components/initiative/InitiativeRow'
import InitiativesHeader from '@/components/initiative/InitiativesHeader.vue'
import InitiativeSkeleton from '@/components/skeletons/InitiativeSkeleton'
import events from '@/constants/events'
import EventBusMixin from '@/mixins/EventBusMixin'
import InitiativesMixin from '@/mixins/InitiativesMixin'
import showAPIError from '@/utils/showAPIError'
import * as R from 'ramda'

/**
 * This component fetches and displays list of initiatives. It handles loader, client side sorting, filtering by itself.
 * Parent function needs to pass a prop - fetcher, which needs to be a function that returns a promise which resolves to give list of initiatives
 */
export default {
  name: 'InitiativesListTable',
  props: {
    fetcher: Function,
    /**
     * Whether the items in the list are selectable. If true, initiative row gets selected on expand/collapse
     */
    selectable: {
      default: true
    },
    /**
     * Whether the tasks inside initiatives are selectable
     */
    selectableTasks: {
      default: true
    }
  },
  components: {
    InitiativesHeader,
    InitiativeSkeleton,
    InitiativeRow
  },
  mixins: [EventBusMixin, InitiativesMixin],
  data() {
    return {
      sortedDirection: '',
      sortedBy: '',
      initiativesCopy: null,
      initiatives: null,
      isFetching: false
    }
  },
  computed: {},
  methods: {
    async handleInitiativeUpdate(initiative) {
      const initiativeIndex = this.initiatives.findIndex(x => x.id === initiative.id)
      const initiatives = R.update(initiativeIndex, initiative, this.initiatives)
      this.resetInitiatives(initiatives)
    },
    resetInitiatives(initiatives) {
      this.initiatives = initiatives
      this.initiativesCopy = [...initiatives]
    },
    sortInitiatives({ by: sortBy, direction: sortDirection }) {
      this.sortedBy = sortBy
      this.sortedDirection = sortDirection
      const orderBy = this.getOrderByFromSortParams({ sortBy, sortDirection })
      this.fetchInitiatives({ orderBy })
    },
    resetSort() {
      this.initiatives = this.initiativesCopy
      this.sortedBy = null
      this.sortedDirection = null
      this.fetchInitiatives()
    },
    async fetchInitiatives(params) {
      try {
        const initiatives = await this.fetcher(params)
        this.resetInitiatives(initiatives)
      } catch (e) {
        showAPIError(e, undefined, 'Error while fetching initiatives')
      }
    },
    onInitiativeUpdate(initiative) {
      const found = this.initiatives.find(R.propEq('id', initiative.id))
      if (found) {
        if (initiative.isClosed) {
          this.fetchInitiatives()
        } else {
          this.handleInitiativeUpdate(initiative)
        }
      }
    }
  },
  async mounted() {
    this.isFetching = true
    await this.fetchInitiatives()
    this.isFetching = false
    this.listenToEventBus(events.INITIATIVE_UPDATED, this.onInitiativeUpdate)
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/_styleConfigurations.scss';

.initiative-skeleton {
  border-bottom: 1px;
  margin-bottom: 16px;
}

.loader {
  padding-left: 45px;
}

.initiative-list {
  @include scrollbarWidth($scrollSecondary);

  overflow-x: auto;
}
</style>
